import { Action } from '@reduxjs/toolkit';
import { Epic, combineEpics } from 'redux-observable';
import {
  EMPTY,
  catchError,
  filter,
  from,
  ignoreElements,
  map,
  mergeMap,
  of,
  withLatestFrom,
} from 'rxjs';

import { eventsAPI } from 'apis/wos';

import { notNull } from 'utils/type_guards';

import utils from './analytics.utils';

import navigation from 'domains/navigation';
import cartBilling from 'domains/cart_billing';
import checkout from 'domains/checkout';

import type { CheckoutRootState } from 'store/checkout_store';

const sendPageViewEventEpic: Epic<Action> = (action$) =>
  action$.pipe(
    filter(navigation.actions.initializeNavigation.match),
    // A ViewContent event is a pageview for a specific product
    map(({ payload }) => (payload.editor ? 'PageView' : 'ViewContent')),
    mergeMap((event) => from(eventsAPI.post('', { event })).pipe(catchError(() => EMPTY))),
    ignoreElements()
  );

const sendInitCheckoutEventEpic: Epic<Action> = (action$) =>
  action$.pipe(
    filter(checkout.actions.initCheckout.fulfilled.match),
    mergeMap(() =>
      from(eventsAPI.post('', { event: 'InitiateCheckout' })).pipe(catchError(() => EMPTY))
    ),
    ignoreElements()
  );

const sendPurchaseAnalyticsEpic: Epic<Action, Action, CheckoutRootState> = (action$, state$) =>
  action$.pipe(
    filter(checkout.actions.submitOrder.fulfilled.match),
    withLatestFrom(state$),
    map(([{ payload }, state]) => {
      utils.submitAnalyticsData(state, payload.order_number);
      return payload;
    }),
    mergeMap((payload) =>
      from(utils.submitMetaConversionData(payload.total)).pipe(
        map(() => checkout.actions.submitOrder.complete(payload.guid)),
        catchError(() => of(checkout.actions.submitOrder.complete(payload.guid)))
      )
    )
  );

/**
 * Analytics for bing UET require their base tag fire a page load event for the page
 * that the custom tag is fired on. This was not working when firing it after the order submit succeeds
 * since the url at that time is /checkout/review, but the base tag fired on /checkout/shipping.
 *
 * The simplest way to resolve this, which also most closely follows their requirements,
 * is to fire the custom event on the confirmation page, which is a full page redirect after
 * the order submits, so it will always have a single page load base event. Microsoft claims
 * any subsequent events will be deduped.
 *
 * This has taken a ton of time & dozens of emails to figure out so we should just leave this alone.
 */
const sendBingEpic: Epic<Action, Action, CheckoutRootState> = (action$, state$) =>
  action$.pipe(
    filter(checkout.actions.initConfirmation.fulfilled.match),
    withLatestFrom(state$),
    map(([, state]) => cartBilling.selectors.billing(state)),
    filter(notNull),
    map(utils.submitBingData),
    ignoreElements()
  );

const combined = combineEpics(
  sendPageViewEventEpic,
  sendInitCheckoutEventEpic,
  sendPurchaseAnalyticsEpic,
  sendBingEpic
);
export default combined;
